import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Tabs,
  TabList,
  Tab,
  Grid,
  AspectRatio,
  Chip,
  CircularProgress,
  Alert,
  Select,
  Option,
  IconButton
} from '@mui/joy';
import {
  Check as CheckIcon,
  Image as ImageIcon,
  Star as StarIcon,
  Star as StarBorderIcon,
  Quotes as QuoteIcon,
  Buildings as BusinessIcon,
  ArrowsLeftRight as CompareIcon
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type {
  ImageTemplate,
  ImageTemplateCategory,
  TemplateDimension
} from '../../types/marketing';

interface TemplateSelectorProps {
  selectedTemplateId: number | null;
  onSelect: (template: ImageTemplate) => void;
  category?: ImageTemplateCategory | 'all';
  showCategoryFilter?: boolean;
  showDimensionFilter?: boolean;
  dimension?: TemplateDimension;
  onDimensionChange?: (dimension: TemplateDimension) => void;
}

// Category display info
const CATEGORY_INFO: Record<
  ImageTemplateCategory | 'all',
  { label: string; icon: React.ReactNode }
> = {
  all: { label: 'All Templates', icon: <ImageIcon /> },
  testimonial: { label: 'Testimonials', icon: <QuoteIcon /> },
  company_post: { label: 'Company Posts', icon: <BusinessIcon /> },
  before_after: { label: 'Before & After', icon: <CompareIcon /> },
  promotion: { label: 'Promotions', icon: <BusinessIcon /> },
  service: { label: 'Services', icon: <BusinessIcon /> }
};

// Dimension display names
const DIMENSION_LABELS: Record<TemplateDimension, string> = {
  instagram_1080x1080: 'Instagram Square (1080x1080)',
  facebook_1200x630: 'Facebook (1200x630)',
  instagram_1080x1920: 'Instagram Story (1080x1920)',
  linkedin_1200x627: 'LinkedIn (1200x627)',
  twitter_1200x675: 'Twitter (1200x675)'
};

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplateId,
  onSelect,
  category = 'all',
  showCategoryFilter = true,
  showDimensionFilter = false,
  dimension = 'instagram_1080x1080',
  onDimensionChange
}) => {
  const [activeCategory, setActiveCategory] = useState<
    ImageTemplateCategory | 'all'
  >(category);
  const [activeDimension, setActiveDimension] =
    useState<TemplateDimension>(dimension);

  const queryClient = useQueryClient();

  // Fetch available templates with preference status
  const {
    data: templates,
    isLoading,
    error
  } = useQuery({
    queryKey: ['image-templates', 'available'],
    queryFn: async () => {
      const response = await api.get('/api/marketing/image-templates/available/');
      return response.data as ImageTemplate[];
    }
  });

  // Toggle favorite mutation
  const favoriteMutation = useMutation({
    mutationFn: async ({
      templateId,
      preferenceId
    }: {
      templateId: number;
      preferenceId?: number;
    }) => {
      if (preferenceId) {
        // Toggle existing preference
        await api.post(
          `/api/marketing/template-preferences/${preferenceId}/toggle-favorite/`,
          {}
        );
      } else {
        // Create new preference as favorite
        await api.post('/api/marketing/template-preferences/', {
          template: templateId,
          is_enabled: true,
          is_favorite: true
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['image-templates'] });
    }
  });

  // Filter templates by category
  const filteredTemplates = React.useMemo(() => {
    if (!templates) return [];

    let filtered = templates;

    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter((t) => t.category === activeCategory);
    }

    // Filter by dimension support
    if (activeDimension) {
      filtered = filtered.filter((t) =>
        t.supported_dimensions.includes(activeDimension)
      );
    }

    // Sort: favorites first, then by sort_order
    return filtered.sort((a, b) => {
      if (a.is_favorite && !b.is_favorite) return -1;
      if (!a.is_favorite && b.is_favorite) return 1;
      return a.sort_order - b.sort_order;
    });
  }, [templates, activeCategory, activeDimension]);

  // Group templates by category for tabs
  const templatesByCategory = React.useMemo(() => {
    if (!templates) return {};

    return templates.reduce(
      (acc, template) => {
        const cat = template.category;
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(template);
        return acc;
      },
      {} as Record<string, ImageTemplate[]>
    );
  }, [templates]);

  const handleDimensionChange = (newDimension: TemplateDimension) => {
    setActiveDimension(newDimension);
    onDimensionChange?.(newDimension);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4
        }}
      >
        <CircularProgress size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="danger">
        Failed to load templates. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Dimension Filter */}
      {showDimensionFilter && (
        <Box sx={{ mb: 2 }}>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            Output Size
          </Typography>
          <Select
            value={activeDimension}
            onChange={(_, value) =>
              value && handleDimensionChange(value as TemplateDimension)
            }
            sx={{ maxWidth: 300 }}
          >
            {Object.entries(DIMENSION_LABELS).map(([key, label]) => (
              <Option key={key} value={key}>
                {label}
              </Option>
            ))}
          </Select>
        </Box>
      )}

      {/* Category Tabs */}
      {showCategoryFilter ? (
        <Tabs
          value={activeCategory}
          onChange={(_, value) =>
            setActiveCategory(value as ImageTemplateCategory | 'all')
          }
          sx={{ mb: 2 }}
        >
          <TabList>
            <Tab value="all">All</Tab>
            {Object.keys(templatesByCategory).map((cat) => (
              <Tab key={cat} value={cat}>
                {CATEGORY_INFO[cat as ImageTemplateCategory]?.label || cat}
              </Tab>
            ))}
          </TabList>
        </Tabs>
      ) : null}

      {/* Template Grid */}
      {filteredTemplates.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.tertiary' }}>
          <ImageIcon size={48} style={{ marginBottom: 8, opacity: 0.5 }} />
          <Typography>No templates available for this selection</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredTemplates.map((template) => {
            const isSelected = selectedTemplateId === template.id;

            return (
              <Grid key={template.id} xs={6} sm={4} md={3}>
                <Card
                  variant={isSelected ? 'solid' : 'outlined'}
                  color={isSelected ? 'primary' : 'neutral'}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: isSelected ? '2px solid' : '1px solid',
                    borderColor: isSelected
                      ? 'primary.500'
                      : 'neutral.outlinedBorder',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: 'md',
                      borderColor: 'primary.300'
                    }
                  }}
                  onClick={() => onSelect(template)}
                >
                  {/* Favorite Button */}
                  <IconButton
                    size="sm"
                    variant="plain"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      zIndex: 1
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      favoriteMutation.mutate({ templateId: template.id });
                    }}
                  >
                    {template.is_favorite ? (
                      <StarIcon color="var(--joy-palette-warning-400)" />
                    ) : (
                      <StarBorderIcon style={{ opacity: 0.5 }} />
                    )}
                  </IconButton>

                  {/* Template Preview */}
                  <AspectRatio ratio="1" sx={{ mb: 1 }}>
                    {template.preview_image_url ? (
                      <Box
                        component="img"
                        src={template.preview_image_url}
                        alt={template.name}
                        sx={{
                          objectFit: 'cover',
                          width: '100%',
                          height: '100%'
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          background:
                            'linear-gradient(135deg, #0a1628 0%, #1e3a5f 100%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 2
                        }}
                      >
                        {/* Category icon */}
                        <Box sx={{ color: '#00bcd4', mb: 1 }}>
                          {CATEGORY_INFO[template.category]?.icon || (
                            <ImageIcon />
                          )}
                        </Box>
                        <Typography
                          level="body-xs"
                          sx={{ color: '#fff', textAlign: 'center' }}
                        >
                          {template.name}
                        </Typography>
                      </Box>
                    )}

                    {/* Selected indicator */}
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          left: 8,
                          bgcolor: 'var(--brand-text, #7013f5)',
                          borderRadius: '50%',
                          p: 0.5,
                          display: 'flex'
                        }}
                      >
                        <CheckIcon size={16} color="#fff" />
                      </Box>
                    )}
                  </AspectRatio>

                  {/* Template Info */}
                  <Box sx={{ px: 1, pb: 1 }}>
                    <Typography
                      level="body-sm"
                      fontWeight="lg"
                      sx={{
                        color: isSelected ? 'primary.plainColor' : 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {template.name}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.5
                      }}
                    >
                      <Chip
                        size="sm"
                        variant="soft"
                        color={isSelected ? 'primary' : 'neutral'}
                      >
                        {template.category_display}
                      </Chip>
                      {template.usage_count && template.usage_count > 0 && (
                        <Typography
                          level="body-xs"
                          sx={{ color: 'text.tertiary' }}
                        >
                          Used {template.usage_count}x
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default TemplateSelector;
